import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Upload, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const ASSIGNMENTS = [];

const statusConfig = {
  pending: { icon: Clock, label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  submitted: { icon: CheckCircle2, label: 'Submitted', className: 'bg-blue-100 text-blue-700' },
  graded: { icon: CheckCircle2, label: 'Graded', className: 'bg-green-100 text-green-700' },
  overdue: { icon: AlertCircle, label: 'Overdue', className: 'bg-red-100 text-red-700' },
};

export default function Assignments() {
  const [uploadingId, setUploadingId] = useState(null);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">Assignments</h1>
        <p className="text-muted-foreground text-sm mt-1">Track and submit your course assignments</p>
      </div>

      <div className="space-y-4">
        {ASSIGNMENTS.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl border p-12 text-center"
          >
            <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-medium text-lg">No Assignments</h3>
            <p className="text-muted-foreground text-sm mt-2">You don't have any assignments at the moment.</p>
          </motion.div>
        ) : (
          ASSIGNMENTS.map((a, i) => {
          const status = statusConfig[a.status];
          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-card rounded-xl border p-5"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">{a.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.course}</p>
                    <p className="text-sm text-muted-foreground mt-2">{a.description}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-muted-foreground">Due: {new Date(a.dueDate).toLocaleDateString()}</span>
                      <Badge className={`${status.className} text-xs border-0`}>
                        <status.icon className="w-3 h-3 mr-1" />
                        {status.label} {a.grade ? `(${a.grade})` : ''}
                      </Badge>
                    </div>
                  </div>
                </div>

                {a.status === 'pending' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 shrink-0"
                    onClick={() => setUploadingId(uploadingId === a.id ? null : a.id)}
                  >
                    <Upload className="w-4 h-4" /> Upload
                  </Button>
                )}
              </div>

              {uploadingId === a.id && (
                <div className="mt-4 p-4 border-2 border-dashed rounded-lg text-center">
                  <Upload className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Drag and drop your file here, or click to browse</p>
                  <Button size="sm" variant="outline" className="mt-3">Choose File</Button>
                </div>
              )}
            </motion.div>
          );
        })
        )}
      </div>
    </motion.div>
  );
}